import {
  DEFAULT_PRESETS,
  clonePreset,
  type InstrumentSlot,
  type SynthPreset,
} from "../../../../src/lib/audio";
import {
  SLOT_BUILTIN_IDS,
  categoryLabel,
  isSynthPreset,
  newPresetId,
  sanitizeBank,
  sanitizePresetCategory,
  sanitizePresetTags,
  type NamedPreset,
  type PresetCategoryId,
} from "./presetBank";

export type InstrumentBankState = {
  presets: SynthPreset[];
  bank: NamedPreset[];
  slotBankIds: Array<string | undefined>;
};

export type InstrumentBankResult = InstrumentBankState & {
  status: string;
  /** Slots whose live preset changed (push to engine). */
  changedSlots: InstrumentSlot[];
};

function cloneState(state: InstrumentBankState): InstrumentBankState {
  return {
    presets: state.presets.map(clonePreset),
    bank: sanitizeBank(state.bank),
    slotBankIds: [...state.slotBankIds],
  };
}

export function initialSlotBankIds(): Array<string | undefined> {
  return [...SLOT_BUILTIN_IDS];
}

export function syncSlotBankIdsFromPresets(state: InstrumentBankState): InstrumentBankState {
  const next = cloneState(state);
  for (let i = 0; i < next.presets.length; i++) {
    const name = next.presets[i]?.name;
    if (!name) {
      continue;
    }
    const match = next.bank.find(entry => entry.name === name);
    if (match) {
      next.slotBankIds[i] = match.id;
    }
  }
  return next;
}

export function applyBankEntry(
  state: InstrumentBankState,
  slot: InstrumentSlot,
  bankId: string,
): InstrumentBankResult | null {
  const entry = state.bank.find(item => item.id === bankId);
  if (!entry) {
    return null;
  }
  const next = cloneState(state);
  next.presets[slot] = clonePreset({ ...entry.preset, name: entry.name });
  next.slotBankIds[slot] = entry.id;
  return {
    ...next,
    status: `Loaded “${entry.name}” into slot ${slot + 1}`,
    changedSlots: [slot],
  };
}

/** Returns null when there is no bank entry to overwrite — caller should run Save as. */
export function saveBankCurrent(
  state: InstrumentBankState,
  slot: InstrumentSlot,
  selectedBankId?: string,
): InstrumentBankResult | null {
  const id = state.slotBankIds[slot] ?? selectedBankId;
  const entryIndex = id ? state.bank.findIndex(item => item.id === id) : -1;
  if (entryIndex < 0) {
    return null;
  }
  const next = cloneState(state);
  const entry = next.bank[entryIndex]!;
  const current = next.presets[slot]!;
  entry.preset = clonePreset({ ...current, name: entry.name });
  next.presets[slot] = clonePreset({ ...current, name: entry.name });
  next.slotBankIds[slot] = entry.id;
  return {
    ...next,
    status: `Saved “${entry.name}”`,
    changedSlots: [slot],
  };
}

export function saveBankAs(
  state: InstrumentBankState,
  slot: InstrumentSlot,
  name: string,
  overwrite: boolean,
): InstrumentBankResult | { conflict: true } | null {
  const trimmed = name.trim();
  if (!trimmed) {
    return null;
  }
  const next = cloneState(state);
  const existing = next.bank.find(entry => entry.name === trimmed);
  if (existing && !overwrite) {
    return { conflict: true };
  }
  if (existing) {
    existing.preset = clonePreset({ ...next.presets[slot]!, name: trimmed });
    next.slotBankIds[slot] = existing.id;
    next.presets[slot] = clonePreset({ ...next.presets[slot]!, name: trimmed });
  } else {
    const id = newPresetId();
    next.bank.push({
      id,
      name: trimmed,
      builtin: false,
      category: "user",
      preset: clonePreset({ ...next.presets[slot]!, name: trimmed }),
    });
    next.slotBankIds[slot] = id;
    next.presets[slot] = clonePreset({ ...next.presets[slot]!, name: trimmed });
  }
  return {
    ...next,
    status: `Saved “${trimmed}”`,
    changedSlots: [slot],
  };
}

export function renameBankEntry(
  state: InstrumentBankState,
  bankId: string,
  name: string,
): InstrumentBankResult | { statusOnly: string } | null {
  const trimmed = name.trim();
  const entryIndex = state.bank.findIndex(item => item.id === bankId);
  if (entryIndex < 0) {
    return null;
  }
  const entry = state.bank[entryIndex]!;
  if (!trimmed || trimmed === entry.name) {
    return null;
  }
  if (state.bank.some(item => item.id !== entry.id && item.name === trimmed)) {
    return { statusOnly: `Name “${trimmed}” is already used` };
  }
  const next = cloneState(state);
  const target = next.bank[entryIndex]!;
  target.name = trimmed;
  target.preset = clonePreset({ ...target.preset, name: trimmed });
  const changedSlots: InstrumentSlot[] = [];
  for (let i = 0; i < next.presets.length; i++) {
    if (next.slotBankIds[i] === bankId) {
      next.presets[i] = clonePreset({ ...next.presets[i]!, name: trimmed });
      changedSlots.push(i as InstrumentSlot);
    }
  }
  return {
    ...next,
    status: `Renamed to “${trimmed}”`,
    changedSlots,
  };
}

export function deleteBankEntry(
  state: InstrumentBankState,
  bankId: string,
): InstrumentBankResult | { statusOnly: string } | null {
  const entry = state.bank.find(item => item.id === bankId);
  if (!entry) {
    return null;
  }
  if (entry.builtin) {
    return { statusOnly: "Factory presets cannot be deleted" };
  }
  const next = cloneState(state);
  next.bank = next.bank.filter(item => item.id !== bankId);
  for (let i = 0; i < next.slotBankIds.length; i++) {
    if (next.slotBankIds[i] === bankId) {
      next.slotBankIds[i] = next.bank[i]?.id ?? next.bank[0]?.id;
    }
  }
  return {
    ...next,
    status: `Deleted “${entry.name}”`,
    changedSlots: [],
  };
}

export function setBankCategory(
  state: InstrumentBankState,
  bankId: string,
  category: PresetCategoryId,
): InstrumentBankResult | null {
  const entryIndex = state.bank.findIndex(item => item.id === bankId);
  if (entryIndex < 0) {
    return null;
  }
  const entry = state.bank[entryIndex]!;
  if (entry.builtin) {
    return null;
  }
  const nextCat = sanitizePresetCategory(category, entry.category);
  if (entry.category === nextCat) {
    return null;
  }
  const next = cloneState(state);
  next.bank[entryIndex]!.category = nextCat;
  return {
    ...next,
    status: `Moved “${entry.name}” to ${categoryLabel(nextCat)}`,
    changedSlots: [],
  };
}

export function setBankTags(
  state: InstrumentBankState,
  bankId: string,
  tagsRaw: string[],
): InstrumentBankResult | null {
  const entryIndex = state.bank.findIndex(item => item.id === bankId);
  if (entryIndex < 0) {
    return null;
  }
  const next = cloneState(state);
  const entry = next.bank[entryIndex]!;
  const tags = sanitizePresetTags(tagsRaw);
  if (tags) {
    entry.tags = tags;
  } else {
    delete entry.tags;
  }
  return {
    ...next,
    status: tags ? `Tagged “${entry.name}” · ${tags.join(", ")}` : `Cleared tags on “${entry.name}”`,
    changedSlots: [],
  };
}

export function resetSlotFactory(
  state: InstrumentBankState,
  slot: InstrumentSlot,
): InstrumentBankResult {
  const factory = DEFAULT_PRESETS[slot]!;
  const next = cloneState(state);
  next.presets[slot] = clonePreset(factory);
  next.slotBankIds[slot] = next.bank[slot]?.id;
  return {
    ...next,
    status: `Reset slot ${slot + 1} to factory ${factory.name}`,
    changedSlots: [slot],
  };
}

export function buildExportEntryPayload(
  state: InstrumentBankState,
  bankId: string | undefined,
  slot: InstrumentSlot,
): { json: string; filename: string; status: string } {
  const entry = bankId ? state.bank.find(item => item.id === bankId) : undefined;
  const payload = entry
    ? { type: "adam-synth-preset", version: 1, preset: entry }
    : {
      type: "adam-synth-preset",
      version: 1,
      preset: {
        id: newPresetId(),
        name: state.presets[slot]!.name,
        preset: state.presets[slot]!,
      },
    };
  const baseName = (entry?.name ?? state.presets[slot]!.name) || "preset";
  const filename = `${baseName.replace(/\s+/g, "-")}.json`;
  return {
    json: JSON.stringify(payload, null, 2),
    filename,
    status: `Exported ${filename}`,
  };
}

export function buildExportAllPayload(state: InstrumentBankState): {
  json: string;
  filename: string;
  status: string;
} {
  const bank = sanitizeBank(state.bank);
  return {
    json: JSON.stringify({ type: "adam-synth-bank", version: 1, bank }, null, 2),
    filename: "adam-synth-bank.json",
    status: `Exported ${bank.length} presets`,
  };
}

export function importBankData(
  state: InstrumentBankState,
  slot: InstrumentSlot,
  data: unknown,
): InstrumentBankResult {
  if (!data || typeof data !== "object") {
    throw new Error("Invalid JSON");
  }
  const obj = data as Record<string, unknown>;

  if (
    (obj.type === "adam-synth-bank" || obj.type === "curva-synth-bank")
    && Array.isArray(obj.bank)
  ) {
    const next = cloneState(state);
    next.bank = sanitizeBank(obj.bank);
    const synced = syncSlotBankIdsFromPresets(next);
    return {
      ...synced,
      status: `Imported bank (${synced.bank.length} presets)`,
      changedSlots: [],
    };
  }

  if (
    (obj.type === "adam-synth-preset" || obj.type === "curva-synth-preset")
    && obj.preset
    && typeof obj.preset === "object"
  ) {
    const wrapped = obj.preset as Partial<NamedPreset>;
    if (wrapped.preset && isSynthPreset(wrapped.preset)) {
      return addImportedNamed(state, slot, {
        id: typeof wrapped.id === "string" ? wrapped.id : newPresetId(),
        name: typeof wrapped.name === "string" ? wrapped.name : wrapped.preset.name || "Imported",
        builtin: false,
        preset: clonePreset(wrapped.preset),
      });
    }
  }

  if (isSynthPreset(obj)) {
    const next = cloneState(state);
    next.presets[slot] = clonePreset(obj);
    return {
      ...next,
      status: `Loaded preset into slot ${slot + 1} (Save as… to add to bank)`,
      changedSlots: [slot],
    };
  }

  if (Array.isArray(obj)) {
    const next = cloneState(state);
    next.bank = sanitizeBank([...next.bank, ...obj]);
    return {
      ...next,
      status: `Merged import into bank (${next.bank.length} presets)`,
      changedSlots: [],
    };
  }

  throw new Error("Unrecognized preset JSON");
}

function addImportedNamed(
  state: InstrumentBankState,
  slot: InstrumentSlot,
  entry: NamedPreset,
): InstrumentBankResult {
  const next = cloneState(state);
  const existingIdx = next.bank.findIndex(
    item => item.id === entry.id || item.name === entry.name,
  );
  const tags = sanitizePresetTags(entry.tags);
  let added: NamedPreset;
  if (existingIdx >= 0 && !next.bank[existingIdx]!.builtin) {
    added = {
      id: next.bank[existingIdx]!.id,
      name: entry.name,
      builtin: false,
      category: sanitizePresetCategory(entry.category, next.bank[existingIdx]!.category),
      ...(tags ? { tags } : {}),
      preset: clonePreset({ ...entry.preset, name: entry.name }),
    };
    next.bank[existingIdx] = added;
  } else {
    added = {
      id: newPresetId(),
      name: entry.name,
      builtin: false,
      category: sanitizePresetCategory(entry.category, "user"),
      ...(tags ? { tags } : {}),
      preset: clonePreset({ ...entry.preset, name: entry.name }),
    };
    next.bank.push(added);
  }
  next.slotBankIds[slot] = added.id;
  next.presets[slot] = clonePreset({ ...added.preset, name: added.name });
  return {
    ...next,
    status: `Imported “${added.name}”`,
    changedSlots: [slot],
  };
}
